// The card deck, dealt in decision-value order.
//
// Ordering by alphabet or popularity is what turns forty useful ratings into two
// hundred useless ones. The deck here is recomputed from the solver: each card is
// an act whose rating would move the schedule, highest mover first, and an act
// that cannot move anything is never dealt.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../data/store.tsx';
import { triageOrder } from '../../engine/decisionValue.ts';
import { fmtTime } from '../../engine/time.ts';
import '../../styles/planning-refinements.css';

export function Triage() {
  const { state, actions, derived } = useApp();
  const [index, setIndex] = useState(0);
  const [seen, setSeen] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const deck = useMemo(() => {
    const rated = new Set(
      state.ratings.filter((r) => r.userId === state.userId && r.source === 'manual').map((r) => r.artistId),
    );
    for (const id of seen) rated.add(id);
    const utility = new Map<string, number>();
    for (const [, dayUtil] of derived.utilityByDay) {
      for (const [setId, value] of dayUtil) utility.set(setId, value);
    }
    return triageOrder(
      state.artists,
      {
        days: derived.dayContexts,
        utility,
        hardLockSetIds: derived.hardLockSetIds,
        rest: derived.restByDay,
        homeStageId: 'stage-camp',
        cfg: state.config,
      },
      rated,
    );
  }, [state.artists, state.ratings, state.userId, state.config, derived, seen]);

  const current = deck[index];

  const answer = async (score: number | null) => {
    if (!current || busy) return;
    setBusy(true);
    setError('');
    try {
      if (score !== null) await actions.rate(current.artist.id, score);
      setSeen((prev) => [...prev, current.artist.id]);
      setIndex(0);
    } catch {
      setError('That rating could not be saved. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!current) {
    return (
      <div className="deck planning-page">
        <h1>Quick ratings</h1>
        <div className="panel">
          <h2>You are caught up</h2>
          <p className="muted">
            There are no more quick-rating suggestions for your current preferences.
          </p>
          <div className="planning-actions"><Link to="/lineup" className="buttonlink">Browse all artists</Link>
            <Link to="/plan" className="buttonlink">Open my plan</Link></div>
        </div>
      </div>
    );
  }

  const upcoming = state.sets
    .filter((s) => s.artistId === current.artist.id)
    .map((s) => `${derived.stageById.get(s.stageId)?.name} ${fmtTime(s.startTs)}`)
    .join(' and ');

  return (
    <div className="deck planning-page triage-page">
      <h1>Quick ratings</h1>
      <p className="lede">Rate a few artists to help refine your suggested schedule.</p>
      {error && <p role="alert" className="planning-error">{error}</p>}
      <div className="cardface">
        <h2>{current.artist.name}</h2>
        <div className="tags">
          {current.artist.genreTags.join(', ') || 'Genre unavailable'}
          {upcoming ? ` · ${upcoming}` : ''}
        </div>
        {current.artist.previewUrl ? (
          <audio controls src={current.artist.previewUrl} style={{ width: '100%' }} />
        ) : (
          <p className="muted" style={{ fontSize: 16, margin: 0 }}>
            Audio preview unavailable.
          </p>
        )}
        <div className="choices">
          <button className="primary" disabled={busy} onClick={() => void answer(9)}>
            Want to see
          </button>
          <button disabled={busy} onClick={() => void answer(null)}>Skip for now</button>
          <button disabled={busy} onClick={() => void answer(2)}>Not for me</button>
        </div>
      </div>
      <div className="progress">
        {deck.length} suggestions left. Want to see saves 9/10; Not for me saves 2/10.
      </div>
      <div className="planning-actions"><Link to="/lineup" className="buttonlink">Browse all artists</Link>
        <Link to="/plan" className="buttonlink">Open my plan</Link></div>
    </div>
  );
}
